import {
    getTopic,
    IClauseInTopicContext,
    ITopicDetail,
    ITopicSection,
    ITopicWithEnabledClauses,
} from "@/api";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { Pill } from "@/components/shared/Pill";
import { ChevronRight, Delete, ExpandMore } from "@mui/icons-material";
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    IconButton,
    Stack,
    styled,
    Tooltip,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { RemoveTopicDialog } from "../dialogs/RemoveTopicDialog";

// Styled components matching frontend ChecklistSectionAccordion pattern
const TopicHeader = styled(Box)<{ isExpanded?: boolean }>(
    ({ isExpanded = false, theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
        padding: "12px 16px",
        paddingRight: "12px",
        marginBottom: isExpanded ? 0 : "8px",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
    })
);

const TopicContent = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    marginBottom: "8px",
    marginTop: 0,
    overflow: "hidden",
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    padding: "8px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

/**
 * Create a compound key for a clause-section combination.
 * Format: "topicClauseId:sectionId" (sectionId is "null" for general section)
 */
const createClauseSectionKey = (
    topicClauseId: number,
    documentSectionTypeId: number | null
): string => {
    return `${topicClauseId}:${documentSectionTypeId ?? "null"}`;
};

interface TopicAccordionProps {
    topic: ITopicWithEnabledClauses;
    clauseChecklistId: number;
    expanded: boolean;
    onToggle: () => void;
    /** Watched clause values from RHF - reactive to form changes */
    clauseValues: Record<string, boolean>;
    onTopicDetailsLoaded: (topicId: number, allKeys: string[]) => void;
    onClauseToggle: (key: string, enabled: boolean) => void;
    onSelectAllTopic: (topicId: number) => void;
    onDeselectAllTopic: (topicId: number) => void;
    onSelectSection: (sectionKeys: string[]) => void;
    onDeselectSection: (sectionKeys: string[]) => void;
}

/**
 * Admin topic accordion for clause checklist clause management.
 * Styled to match frontend expandable panel pattern.
 */
export const TopicAccordion = ({
    topic,
    clauseChecklistId,
    expanded,
    onToggle,
    clauseValues,
    onTopicDetailsLoaded,
    onClauseToggle,
    onSelectAllTopic,
    onDeselectAllTopic,
    onSelectSection,
    onDeselectSection,
}: TopicAccordionProps) => {
    const dialogs = useDialogs();

    // Lazy load topic details only when expanded
    const {
        data: topicDetail,
        isPending,
        error,
    } = useQuery({
        queryKey: ["admin-topic-detail", topic.id],
        queryFn: () => getTopic(topic.id),
        enabled: expanded,
    });

    // Get all clause-section keys when topic details load
    const allClauseSectionKeys = useMemo(() => {
        if (!topicDetail) return [];
        const keys: string[] = [];
        topicDetail.clauses_by_section.forEach((section) => {
            const sectionId = section.document_section_type?.id ?? null;
            section.clauses.forEach((clause) => {
                keys.push(
                    createClauseSectionKey(clause.topic_clause_id, sectionId)
                );
            });
        });
        return keys;
    }, [topicDetail]);

    // Notify parent when topic details load to populate form
    useEffect(() => {
        if (topicDetail && allClauseSectionKeys.length > 0) {
            onTopicDetailsLoaded(topic.id, allClauseSectionKeys);
        }
    }, [topicDetail, allClauseSectionKeys, topic.id, onTopicDetailsLoaded]);

    // Calculate total clause-section count and enabled count from form values
    const { totalClauseSectionCount, enabledCount } = useMemo(() => {
        const enabledKeys = allClauseSectionKeys.filter(
            (key) => clauseValues[key]
        );

        return {
            totalClauseSectionCount: topic.clause_count,
            enabledCount:
                allClauseSectionKeys.length > 0
                    ? enabledKeys.length
                    : topic.enabled_clauses.length > 0
                      ? topic.enabled_clauses.length
                      : topic.clause_count, // Default to all enabled when not loaded
        };
    }, [
        allClauseSectionKeys,
        clauseValues,
        topic.clause_count,
        topic.enabled_clauses.length,
    ]);

    const handleRemoveTopic = (e: React.MouseEvent) => {
        e.stopPropagation();
        dialogs.open(RemoveTopicDialog, {
            clauseChecklistId,
            topicId: topic.id,
        });
    };

    return (
        <Box sx={{ width: "100%" }}>
            <TopicHeader isExpanded={expanded} onClick={onToggle}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        flex: 1,
                        gap: "0.5rem",
                        maxWidth: "calc(100% - 80px)",
                    }}
                >
                    <Link
                        to={`/admin/topics/${topic.id}/details`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: "none", color: "inherit" }}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                                lineHeight: 1.4,
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            {topic.name}
                        </Typography>
                    </Link>
                    <Chip
                        label={`${enabledCount}/${totalClauseSectionCount} enabled`}
                        size="small"
                        sx={{
                            bgcolor: "rgba(255, 255, 255, 0.2)",
                            color: "inherit",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                        }}
                    />
                </Box>

                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ ml: 1 }}
                >
                    <Tooltip title="Remove Topic">
                        <IconButton
                            size="small"
                            onClick={handleRemoveTopic}
                            sx={{
                                color: "inherit",
                                padding: "4px",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                                },
                            }}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        size="small"
                        sx={{
                            color: "inherit",
                            padding: "4px",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                            },
                        }}
                    >
                        {expanded ? <ExpandMore /> : <ChevronRight />}
                    </IconButton>
                </Stack>
            </TopicHeader>

            <Collapse in={expanded}>
                <TopicContent>
                    {isPending && (
                        <Box p={3}>
                            <LoadingIndicator />
                        </Box>
                    )}
                    {error && (
                        <Box p={2}>
                            <Alert severity="error" variant="outlined">
                                Failed to load topic details. Please try again.
                            </Alert>
                        </Box>
                    )}
                    {topicDetail && (
                        <TopicClausesList
                            topicDetail={topicDetail}
                            topicId={topic.id}
                            allClauseSectionKeys={allClauseSectionKeys}
                            totalClauseSectionCount={totalClauseSectionCount}
                            clauseValues={clauseValues}
                            onClauseToggle={onClauseToggle}
                            onSelectAllTopic={onSelectAllTopic}
                            onDeselectAllTopic={onDeselectAllTopic}
                            onSelectSection={onSelectSection}
                            onDeselectSection={onDeselectSection}
                        />
                    )}
                </TopicContent>
            </Collapse>
        </Box>
    );
};

interface TopicClausesListProps {
    topicDetail: ITopicDetail;
    topicId: number;
    allClauseSectionKeys: string[];
    totalClauseSectionCount: number;
    /** Watched clause values from RHF - reactive to form changes */
    clauseValues: Record<string, boolean>;
    onClauseToggle: (key: string, enabled: boolean) => void;
    onSelectAllTopic: (topicId: number) => void;
    onDeselectAllTopic: (topicId: number) => void;
    onSelectSection: (sectionKeys: string[]) => void;
    onDeselectSection: (sectionKeys: string[]) => void;
}

interface PolicyTypeOption {
    id: number;
    name: string;
    shortName: string;
}

/**
 * Displays clauses grouped by section with checkboxes.
 */
const TopicClausesList = ({
    topicDetail,
    topicId,
    allClauseSectionKeys,
    totalClauseSectionCount,
    clauseValues,
    onClauseToggle,
    onSelectAllTopic,
    onDeselectAllTopic,
    onSelectSection,
    onDeselectSection,
}: TopicClausesListProps) => {
    // Compute available policy types from clauses
    const availablePolicyTypes = useMemo(() => {
        const policyTypeMap = new Map<number, PolicyTypeOption>();

        topicDetail.clauses_by_section.forEach((section) => {
            section.clauses.forEach((clause) => {
                if (
                    clause.policy_type &&
                    !policyTypeMap.has(clause.policy_type.id)
                ) {
                    policyTypeMap.set(clause.policy_type.id, {
                        id: clause.policy_type.id,
                        name: clause.policy_type.name,
                        shortName: clause.policy_type.short_name,
                    });
                }
            });
        });

        return Array.from(policyTypeMap.values()).sort((a, b) =>
            a.shortName.localeCompare(b.shortName)
        );
    }, [topicDetail.clauses_by_section]);

    // Check if a clause-section is enabled from form values
    const isClauseSectionEnabled = (
        topicClauseId: number,
        documentSectionTypeId: number | null
    ): boolean => {
        const key = createClauseSectionKey(
            topicClauseId,
            documentSectionTypeId
        );
        return clauseValues[key] ?? false;
    };

    // Calculate enabled count from form values
    const enabledCount = allClauseSectionKeys.filter(
        (key) => clauseValues[key]
    ).length;
    const allSelected = enabledCount === totalClauseSectionCount;
    const noneSelected = enabledCount === 0;

    const handleSelectAll = () => {
        onSelectAllTopic(topicId);
    };

    const handleDeselectAll = () => {
        onDeselectAllTopic(topicId);
    };

    // Get keys for a specific policy type
    const getKeysForPolicyType = (policyTypeId: number): string[] => {
        const keys: string[] = [];
        topicDetail.clauses_by_section.forEach((section) => {
            const sectionId = section.document_section_type?.id ?? null;
            section.clauses.forEach((clause) => {
                if (clause.policy_type?.id === policyTypeId) {
                    keys.push(
                        createClauseSectionKey(
                            clause.topic_clause_id,
                            sectionId
                        )
                    );
                }
            });
        });
        return keys;
    };

    // Check if a policy type is currently the only one selected
    const isPolicyTypeActive = (policyTypeId: number): boolean => {
        const policyTypeKeys = getKeysForPolicyType(policyTypeId);
        if (policyTypeKeys.length === 0) return false;

        // Check if all policy type keys are enabled
        const allPolicyTypeEnabled = policyTypeKeys.every(
            (key) => clauseValues[key]
        );
        if (!allPolicyTypeEnabled) return false;

        // Check if only these keys are enabled (no others)
        const enabledKeys = allClauseSectionKeys.filter(
            (key) => clauseValues[key]
        );
        return enabledKeys.length === policyTypeKeys.length;
    };

    // Handler for toggling clauses by policy type
    const handleTogglePolicyType = (policyTypeId: number) => {
        const policyTypeKeys = getKeysForPolicyType(policyTypeId);

        if (isPolicyTypeActive(policyTypeId)) {
            // Already active - deselect all
            onDeselectSection(policyTypeKeys);
        } else {
            // Not active - deselect all, then select only this policy type
            onDeselectSection(allClauseSectionKeys);
            onSelectSection(policyTypeKeys);
        }
    };

    // Filter to sections with clauses
    const sectionsWithClauses = topicDetail.clauses_by_section.filter(
        (section) => section.clauses.length > 0
    );

    return (
        <Stack>
            {/* Select All / Deselect All / Policy Type toolbar */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                            cursor: allSelected ? "default" : "pointer",
                            opacity: allSelected ? 0.5 : 1,
                            fontWeight: 500,
                            "&:hover": {
                                textDecoration: allSelected
                                    ? "none"
                                    : "underline",
                            },
                        }}
                        onClick={allSelected ? undefined : handleSelectAll}
                    >
                        Select All
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        |
                    </Typography>
                    <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                            cursor: noneSelected ? "default" : "pointer",
                            opacity: noneSelected ? 0.5 : 1,
                            fontWeight: 500,
                            "&:hover": {
                                textDecoration: noneSelected
                                    ? "none"
                                    : "underline",
                            },
                        }}
                        onClick={noneSelected ? undefined : handleDeselectAll}
                    >
                        Deselect All
                    </Typography>

                    {/* Policy Type toggle shortcuts */}
                    {availablePolicyTypes.length > 0 && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                |
                            </Typography>
                            {availablePolicyTypes.map((pt, index) => {
                                const isActive = isPolicyTypeActive(pt.id);
                                return (
                                    <Stack
                                        key={pt.id}
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        {index > 0 && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                |
                                            </Typography>
                                        )}
                                        <Tooltip
                                            title={
                                                isActive
                                                    ? `Deselect ${pt.name} clauses`
                                                    : `Select only ${pt.name} clauses`
                                            }
                                        >
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                                sx={{
                                                    cursor: "pointer",
                                                    fontWeight: isActive
                                                        ? 700
                                                        : 500,
                                                    textDecoration: isActive
                                                        ? "underline"
                                                        : "none",
                                                    "&:hover": {
                                                        textDecoration:
                                                            "underline",
                                                    },
                                                }}
                                                onClick={() =>
                                                    handleTogglePolicyType(
                                                        pt.id
                                                    )
                                                }
                                            >
                                                {pt.shortName}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                );
                            })}
                        </>
                    )}
                </Stack>
            </Box>

            {/* Sections with clauses */}
            {sectionsWithClauses.map((section, index) => (
                <SectionClauses
                    key={
                        section.document_section_type?.id ?? `general-${index}`
                    }
                    section={section}
                    isClauseSectionEnabled={isClauseSectionEnabled}
                    onClauseToggle={onClauseToggle}
                    onSelectSection={onSelectSection}
                    onDeselectSection={onDeselectSection}
                />
            ))}

            {/* Empty state */}
            {sectionsWithClauses.length === 0 && (
                <Box p={2}>
                    <Typography variant="body2" color="text.secondary">
                        No clauses in this topic.
                    </Typography>
                </Box>
            )}
        </Stack>
    );
};

interface SectionClausesProps {
    section: ITopicSection;
    isClauseSectionEnabled: (
        topicClauseId: number,
        documentSectionTypeId: number | null
    ) => boolean;
    onClauseToggle: (key: string, enabled: boolean) => void;
    onSelectSection: (sectionKeys: string[]) => void;
    onDeselectSection: (sectionKeys: string[]) => void;
}

/**
 * Displays clauses within a document section.
 */
const SectionClauses = ({
    section,
    isClauseSectionEnabled,
    onClauseToggle,
    onSelectSection,
    onDeselectSection,
}: SectionClausesProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const sectionName = section.document_section_type?.name ?? "General";
    const documentSectionTypeId = section.document_section_type?.id ?? null;

    // Calculate section clause keys and enabled state
    const sectionClauseKeys = useMemo(() => {
        return section.clauses.map((clause) =>
            createClauseSectionKey(
                clause.topic_clause_id,
                documentSectionTypeId
            )
        );
    }, [section.clauses, documentSectionTypeId]);

    const enabledCount = useMemo(() => {
        return section.clauses.filter((clause) =>
            isClauseSectionEnabled(
                clause.topic_clause_id,
                documentSectionTypeId
            )
        ).length;
    }, [section.clauses, isClauseSectionEnabled, documentSectionTypeId]);

    const allSelected = enabledCount === section.clauses.length;
    const noneSelected = enabledCount === 0;
    const isIndeterminate = !allSelected && !noneSelected;

    const handleSectionCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        event.stopPropagation();
        if (allSelected) {
            // Deselect all section clauses
            onDeselectSection(sectionClauseKeys);
        } else {
            // Select all section clauses (handles both indeterminate and unchecked)
            onSelectSection(sectionClauseKeys);
        }
    };

    const handleCheckboxClick = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    if (section.clauses.length === 0) {
        return null;
    }

    return (
        <Box>
            {/* Section header */}
            <SectionHeader
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ cursor: "pointer", "&:hover": { bgcolor: "grey.200" } }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox
                            checked={allSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSectionCheckboxChange}
                            onClick={handleCheckboxClick}
                            size="small"
                            sx={{ p: 0 }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                            {sectionName}
                        </Typography>
                        <Chip
                            label={`${enabledCount}/${section.clauses.length} clause${section.clauses.length !== 1 ? "s" : ""}`}
                            size="small"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                    </Stack>
                    <IconButton size="small" sx={{ p: 0.5 }}>
                        {isExpanded ? (
                            <ExpandMore fontSize="small" />
                        ) : (
                            <ChevronRight fontSize="small" />
                        )}
                    </IconButton>
                </Stack>
            </SectionHeader>

            {/* Clause checkboxes */}
            <Collapse in={isExpanded}>
                <Stack>
                    {section.clauses.map((clause) => {
                        const clauseKey = createClauseSectionKey(
                            clause.topic_clause_id,
                            documentSectionTypeId
                        );
                        return (
                            <ClauseCheckbox
                                key={clauseKey}
                                clause={clause}
                                clauseKey={clauseKey}
                                checked={isClauseSectionEnabled(
                                    clause.topic_clause_id,
                                    documentSectionTypeId
                                )}
                                onToggle={onClauseToggle}
                            />
                        );
                    })}
                </Stack>
            </Collapse>
        </Box>
    );
};

interface ClauseCheckboxProps {
    clause: IClauseInTopicContext;
    clauseKey: string;
    checked: boolean;
    onToggle: (key: string, enabled: boolean) => void;
}

/**
 * Single clause checkbox item.
 */
const ClauseCheckbox = ({
    clause,
    clauseKey,
    checked,
    onToggle,
}: ClauseCheckboxProps) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onToggle(clauseKey, event.target.checked);
    };

    return (
        <Box
            sx={{
                px: 2,
                py: 0.75,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:last-child": { borderBottom: 0 },
                "&:hover": { bgcolor: "action.hover" },
            }}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={checked}
                        onChange={handleChange}
                        size="small"
                        sx={{ py: 0 }}
                    />
                }
                label={
                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                        {clause.title}
                    </Typography>
                }
                sx={{ m: 0, alignItems: "flex-start" }}
            />

            {clause.policy_type && <Pill label={clause.policy_type.name} />}
        </Box>
    );
};
